import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import {
  connectFirestoreEmulator,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  clearIndexedDbPersistence,
  waitForPendingWrites,
} from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage, connectStorageEmulator } from 'firebase/storage';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

/**
 * Banco Firestore da aplicação — nomeado, em southamerica-east1.
 *
 * O `(default)` do projeto nasceu em `nam5` e a localização do Firestore é
 * permanente; dados de pacientes brasileiros ficam em São Paulo (PLANO seção
 * 9). Declarado aqui e em functions/lib/admin.js pelo mesmo motivo do
 * telefone.js: o deploy das Functions empacota só a pasta functions/.
 */
export const BANCO_FIRESTORE = 'odontosdr-sp';

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
/**
 * ★ VL — CACHE LOCAL PERSISTENTE, com várias abas. Depois da V7 a coleção de
 * leads passa de 7 mil documentos (4–6 MB no primeiro `onSnapshot`); sem cache,
 * cada aba nova baixa tudo de novo. Com o cache, a segunda abertura lê o delta.
 * `persistentMultipleTabManager` é obrigatório aqui: o dono deixa o sistema
 * aberto em duas abas, e o gerenciador de aba única falharia com
 * `failed-precondition` na segunda. Onde não há IndexedDB (testes, Node), o
 * SDK cai no cache em memória sozinho.
 */
export const db = initializeFirestore(
  app,
  { localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }) },
  BANCO_FIRESTORE,
);
export const functions = getFunctions(app, 'southamerica-east1');
export const storage = getStorage(app);

// Em desenvolvimento o padrão é conversar com os emuladores. Só sai desse modo
// quem definir explicitamente VITE_USE_EMULATORS=false — assim nenhum teste
// local escreve por acidente no projeto de produção.
export const usandoEmuladores =
  import.meta.env.VITE_USE_EMULATORS !== 'false' && import.meta.env.DEV;

if (usandoEmuladores && !globalThis.__odontosdrEmuladoresConectados) {
  globalThis.__odontosdrEmuladoresConectados = true;
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
  connectStorageEmulator(storage, '127.0.0.1', 9199);
  console.info('[OdontoSDR] Conectado aos emuladores locais do Firebase.');
}

/**
 * ★★★ WU/U3 — A RENOVAÇÃO DA CÓPIA LOCAL, E A ORDEM QUE ELA EXIGE.
 *
 * ⚠⚠ ESTA É UMA ORDEM DELICADA, e ela tem dois donos:
 *
 *  - ★ **VL** precisa abrir o cache **cedo**, na carga da página: medido em Chrome headless, se
 *    a página é trocada enquanto o SDK cria o esquema do IndexedDB, toda aba fica presa em
 *    "Carregando…" até fechar TODAS (0/8 nos testes). `waitForPendingWrites` é a chamada mais
 *    barata que inicializa o cliente.
 *  - ★★★ **U3** precisa limpar o cache **antes de o cliente ser inicializado**:
 *    `clearIndexedDbPersistence` só vale antes de qualquer uso, ou depois de `terminate()`
 *    (que mata todo listener e não volta).
 *
 * ⚠⚠ **As duas não podem ser a primeira.** ★★ A ordem escolhida: **limpar primeiro, abrir
 * depois** — porque o custo de errar é assimétrico. ★ Abrir 50 ms mais tarde reabre a janela da
 * VL por 50 ms; ⚠⚠ limpar depois de abrir **não limpa nada** e o U3 inteiro não existe.
 *
 * ★ `copiaLocalPronta` é a promessa que os dois compartilham. Quem precisa saber se a limpeza
 * falhou por outra aba aberta (`AppShell`, para mostrar a faixa) a aguarda.
 */
export const copiaLocalPronta = (async () => {
  let resultado = { limpou: false, motivo: null, outraAba: false };
  try {
    const [{ renovarCopiaSeNecessario }, { ultimoUid }] = await Promise.all([
      import('./copiaLocal.js'),
      import('./ultimoUid.js'),
    ]);
    /**
     * ⚠⚠ `versao` NÃO é mais passada — o gatilho por versão foi removido em 07/out/2026.
     * ★★★ Ele apagava o IndexedDB compartilhado e o SDK **encerrava o cliente das abas já
     * abertas**. Ver `copiaLocal.js` e `docs/wu-falha-duas-abas.md`.
     */
    resultado = await renovarCopiaSeNecessario({
      uid: ultimoUid(),
      limpar: () => clearIndexedDbPersistence(db),
    });
  } catch {
    // ⚠ Nada aqui pode derrubar o app. Cache que não renovou é pior que renovado, não fatal.
  }

  /**
   * ★ VL — ABRE O CACHE PERSISTENTE, agora que a limpeza já decidiu.
   *
   * Medido em Chrome headless (docs/vl-testes.md): na PRIMEIRA visita de um navegador o SDK cria
   * o esquema do IndexedDB numa transação `versionchange`. Se a página é trocada nesse instante
   * (reload, link), o Chrome guarda a página antiga no back/forward cache com essa transação
   * pendente, e a página nova — e toda aba aberta depois — fica presa em "Carregando…" até
   * fechar TODAS as abas. Sem bfcache, 8/8; com o esquema já criado, 8/8; reload durante a
   * criação, 0/8. O erro é engolido porque um cache que não abriu não pode derrubar o app.
   */
  waitForPendingWrites(db).catch(() => {});
  return resultado;
})();
